import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Search, User, Pause, Calendar, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';

const FILTER_TABS = [
  { key: 'all', label: '전체' },
  { key: 'active', label: '활성' },
  { key: 'expired', label: '만료' },
  { key: 'imminent', label: '임박' },
  { key: 'holding', label: '홀딩' },
  { key: 'upcoming', label: '예정' },
];

const Members = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [holdingDialogOpen, setHoldingDialogOpen] = useState(false);
  const [holdingDates, setHoldingDates] = useState({
    start: '',
    end: ''
  });
  const [holdingLoading, setHoldingLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    pin: '1234',
    birth_date: '',
    gender: '',
    body_weight_kg: ''
  });

  // Filter counts
  const filterCounts = useMemo(() => {
    const counts = {
      all: members.length,
      active: 0,
      expired: 0,
      imminent: 0,
      holding: 0,
      upcoming: 0,
    };
    
    members.forEach(m => {
      if (m.has_active && !m.is_holding) counts.active++;
      if (m.all_expired) counts.expired++;
      if (m.is_imminent && !m.is_holding) counts.imminent++;
      if (m.is_holding) counts.holding++;
      if (m.has_upcoming) counts.upcoming++;
    });
    
    return counts;
  }, [members]);

  // Filtered members based on search and filter
  const filteredMembers = useMemo(() => {
    let result = members;
    
    // Apply filter tab
    if (activeFilter !== 'all') {
      result = result.filter(m => {
        switch (activeFilter) {
          case 'active': return m.has_active && !m.is_holding;
          case 'expired': return m.all_expired;
          case 'imminent': return m.is_imminent && !m.is_holding;
          case 'holding': return m.is_holding;
          case 'upcoming': return m.has_upcoming;
          default: return true;
        }
      });
    }
    
    // Apply search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(m => 
        m.name?.toLowerCase().includes(q) || 
        m.phone?.includes(q) ||
        m.member_number?.toLowerCase().includes(q)
      );
    }
    
    return result;
  }, [members, activeFilter, searchQuery]);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/members/enriched');
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error('회원 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (data.body_weight_kg) {
        data.body_weight_kg = parseFloat(data.body_weight_kg);
      } else {
        delete data.body_weight_kg;
      }
      await apiClient.post('/admin/members', data);
      toast.success('회원이 등록되었습니다');
      setDialogOpen(false);
      setFormData({ name: '', phone: '', email: '', pin: '1234', birth_date: '', gender: '', body_weight_kg: '' });
      fetchMembers();
    } catch (error) {
      console.error('Failed to create member:', error);
      toast.error('회원 등록 실패');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMembers(filteredMembers.map(m => m._id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSelectMember = (memberId, checked) => {
    if (checked) {
      setSelectedMembers(prev => [...prev, memberId]);
    } else {
      setSelectedMembers(prev => prev.filter(id => id !== memberId));
    }
  };

  const handleBatchHold = async () => {
    if (!holdingDates.start || !holdingDates.end) {
      toast.error('홀딩 시작일과 종료일을 모두 입력해주세요');
      return;
    }

    if (new Date(holdingDates.end) < new Date(holdingDates.start)) {
      toast.error('홀딩 종료일은 시작일 이후여야 합니다');
      return;
    }

    if (selectedMembers.length === 0) {
      toast.error('홀딩 적용할 회원을 선택해주세요');
      return;
    }

    setHoldingLoading(true);
    try {
      const response = await apiClient.post('/admin/passes/batch-hold', {
        member_ids: selectedMembers,
        holding_start: holdingDates.start,
        holding_end: holdingDates.end
      });

      const { total_passes_updated, holding_days } = response.data;
      toast.success(`${selectedMembers.length}명의 회원, ${total_passes_updated}개 이용권에 ${holding_days}일 홀딩이 적용되었습니다`);
      
      setHoldingDialogOpen(false);
      setHoldingDates({ start: '', end: '' });
      setSelectedMembers([]);
      fetchMembers();
    } catch (error) {
      console.error('Batch hold failed:', error);
      toast.error('일괄 홀딩 적용 실패: ' + (error.response?.data?.detail || error.message));
    } finally {
      setHoldingLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">회원 관리</h1>
        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                회원 등록
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleCreateMember}>
                <DialogHeader>
                  <DialogTitle>신규 회원 등록</DialogTitle>
                  <DialogDescription>
                    회원 정보를 입력해주세요.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">이름 *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="phone">전화번호 *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="010-0000-0000"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="birth_date">생년월일</Label>
                      <Input
                        id="birth_date"
                        type="date"
                        value={formData.birth_date}
                        onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="gender">성별</Label>
                      <select
                        id="gender"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={formData.gender}
                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      >
                        <option value="">선택</option>
                        <option value="male">남성</option>
                        <option value="female">여성</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="body_weight_kg">체중 (kg)</Label>
                    <Input
                      id="body_weight_kg"
                      type="number"
                      step="0.1"
                      min="30"
                      max="200"
                      placeholder="예: 70.5"
                      value={formData.body_weight_kg}
                      onChange={(e) => setFormData({ ...formData, body_weight_kg: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email">이메일</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="pin">PIN (초기 비밀번호)</Label>
                    <Input
                      id="pin"
                      value={formData.pin}
                      onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                      maxLength="4"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit">등록</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={holdingDialogOpen} onOpenChange={setHoldingDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={selectedMembers.length === 0}>
                <Pause className="mr-2 h-4 w-4" />
                일괄 홀딩
                {selectedMembers.length > 0 && ` (${selectedMembers.length})`}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>일괄 홀딩 적용</DialogTitle>
                <DialogDescription>
                  선택한 {selectedMembers.length}명의 회원에게 홀딩을 적용합니다.
                  홀딩 기간만큼 이용권 종료일이 연장됩니다.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="holding_start">홀딩 시작일</Label>
                  <Input
                    id="holding_start"
                    type="date"
                    value={holdingDates.start}
                    onChange={(e) => setHoldingDates(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="holding_end">홀딩 종료일</Label>
                  <Input
                    id="holding_end"
                    type="date"
                    value={holdingDates.end}
                    onChange={(e) => setHoldingDates(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
                {holdingDates.start && holdingDates.end && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                    <Calendar className="inline h-4 w-4 mr-1" />
                    홀딩 기간: {Math.ceil((new Date(holdingDates.end) - new Date(holdingDates.start)) / (1000 * 60 * 60 * 24)) + 1}일
                    <br />
                    이용권 종료일이 해당 기간만큼 연장됩니다.
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setHoldingDialogOpen(false)}>
                  취소
                </Button>
                <Button onClick={handleBatchHold} disabled={holdingLoading}>
                  {holdingLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      처리 중...
                    </>
                  ) : (
                    '홀딩 적용'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTER_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveFilter(tab.key);
              setSelectedMembers([]);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === tab.key
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.label} {filterCounts[tab.key]}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-10"
            placeholder="회원명, 전화번호, 회원번호로 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Member Table */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-lg">
            {FILTER_TABS.find(t => t.key === activeFilter)?.label || '전체'} 회원 ({filteredMembers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Horizontal scrollable table container */}
          <div className="overflow-x-auto max-h-[600px]" style={{ overflowY: 'auto' }}>
            <table className="w-full min-w-[1400px] text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="border-b">
                  <th className="p-3 text-left w-12">
                    <Checkbox
                      checked={selectedMembers.length === filteredMembers.length && filteredMembers.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="p-3 text-left whitespace-nowrap">회원번호</th>
                  <th className="p-3 text-left whitespace-nowrap">이름</th>
                  <th className="p-3 text-left whitespace-nowrap">전화번호</th>
                  <th className="p-3 text-left whitespace-nowrap">성별</th>
                  <th className="p-3 text-left whitespace-nowrap">나이</th>
                  <th className="p-3 text-left whitespace-nowrap min-w-[200px]">보유 이용권</th>
                  <th className="p-3 text-left whitespace-nowrap">최초 등록일</th>
                  <th className="p-3 text-left whitespace-nowrap">최종 만료일</th>
                  <th className="p-3 text-left whitespace-nowrap">남은 일수</th>
                  <th className="p-3 text-left whitespace-nowrap">최근 출석일</th>
                  <th className="p-3 text-left whitespace-nowrap min-w-[150px]">기타 메모</th>
                  <th className="p-3 text-left whitespace-nowrap">상태</th>
                  <th className="p-3 text-left whitespace-nowrap">액션</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="p-8 text-center text-gray-500">
                      조건에 맞는 회원이 없습니다
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member) => (
                    <tr 
                      key={member._id} 
                      className={`border-b hover:bg-gray-50 cursor-pointer ${
                        selectedMembers.includes(member._id) ? 'bg-blue-50' : ''
                      }`}
                      onDoubleClick={() => navigate(`/admin/members/${member._id}`)}
                    >
                      <td className="p-3">
                        <Checkbox
                          checked={selectedMembers.includes(member._id)}
                          onCheckedChange={(checked) => handleSelectMember(member._id, checked)}
                        />
                      </td>
                      <td className="p-3 font-mono text-xs">{member.member_number}</td>
                      <td className="p-3 font-medium">{member.name}</td>
                      <td className="p-3 text-gray-600">{member.phone}</td>
                      <td className="p-3">{member.gender_display}</td>
                      <td className="p-3">{member.age ?? '-'}</td>
                      <td className="p-3 min-w-[200px]">
                        {(!member.active_passes || member.active_passes.length === 0) ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {member.active_passes.map((pass, idx) => (
                              <span 
                                key={idx}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                  pass.is_holding
                                    ? 'bg-purple-100 text-purple-700'
                                    : pass.pass_type === 'count'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {pass.name}
                                {pass.is_holding && ' (홀딩)'}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-gray-600">{formatDate(member.created_at)}</td>
                      <td className="p-3 text-gray-600">{formatDate(member.latest_end_date)}</td>
                      <td className="p-3">
                        {member.days_remaining !== null ? (
                          <span className={`font-medium ${
                            member.days_remaining <= 10 ? 'text-red-600' :
                            member.days_remaining <= 30 ? 'text-orange-600' : 'text-green-600'
                          }`}>
                            {member.days_remaining}일
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-gray-600">{member.last_attendance_date || '-'}</td>
                      <td className="p-3">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-gray-500 text-xs cursor-default">
                                {member.memo && member.memo.length > 15 
                                  ? member.memo.substring(0, 15) + '...'
                                  : member.memo || '-'}
                              </span>
                            </TooltipTrigger>
                            {member.memo && member.memo.length > 15 && (
                              <TooltipContent>
                                <p className="max-w-xs whitespace-pre-wrap">{member.memo}</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                      <td className="p-3">
                        {member.is_holding ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                            홀딩
                          </span>
                        ) : member.has_active ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            활동
                          </span>
                        ) : member.has_upcoming ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                            예정
                          </span>
                        ) : member.all_expired ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                            만료
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                            -
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/members/${member._id}`)}
                        >
                          <User className="h-4 w-4 mr-1" />
                          상세
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Members;
